import { useMemo, useState } from "react";
import { events, eventFilterOptions } from "@/data/events";
import { getTrackById } from "@/data/tracks";
import { EVENTS } from "@/constants/testIds";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, ArrowRight } from "lucide-react";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function FeaturedEvents() {
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    return events.filter((e) => e.category === filter);
  }, [filter]);

  const handleEventCta = (ev) => {
    toast.success(`${ev.ctaLabel}: ${ev.title}`, {
      description:
        "We've noted your interest. Please share details in the enquiry form for a personalized reply.",
    });
    scrollTo("enquiry");
  };

  return (
    <section
      id="events"
      data-testid={EVENTS.section}
      className="py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="eyebrow">Featured Events</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 leading-[1.05] max-w-2xl">
              Upcoming and planned Aura events
            </h2>
          </div>
          <p className="font-body text-[var(--aura-charcoal-2)] max-w-md">
            Editable placeholders. Dates, hosts, and venues update as each event
            goes live.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {eventFilterOptions.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={EVENTS.filterChip(f.value)}
                className={`font-ui text-sm px-4 py-2 rounded-full border transition-all ${
                  active
                    ? "bg-[var(--aura-teal)] text-[var(--aura-ivory)] border-[var(--aura-teal)]"
                    : "bg-white text-[var(--aura-charcoal-2)] border-black/10 hover:border-[var(--aura-teal)]/40"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((ev) => {
            const track = getTrackById(ev.trackId);
            return (
              <article
                key={ev.id}
                data-testid={EVENTS.card(ev.id)}
                className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-black/5 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={ev.image}
                    alt={`${ev.title} — ${ev.category} event`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"
                  />
                  <span
                    className="absolute top-4 left-4 font-ui text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full backdrop-blur-md"
                    style={{
                      backgroundColor: track?.theme?.chipBg || "#0E4749",
                      color: track?.theme?.chipText || "#FAF9F6",
                    }}
                  >
                    {ev.category}
                  </span>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl leading-snug">
                    {ev.title}
                  </h3>
                  <p className="mt-2 font-body text-sm text-[var(--aura-charcoal-2)] leading-relaxed">
                    {ev.description}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs font-ui text-[var(--aura-muted)]">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={13} /> {ev.date}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={13} /> {ev.duration}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={13} /> {ev.mode}
                    </span>
                  </div>

                  <button
                    onClick={() => handleEventCta(ev)}
                    data-testid={EVENTS.cardCta(ev.id)}
                    className="mt-6 inline-flex items-center justify-center gap-1.5 font-ui text-sm px-5 py-2.5 rounded-full bg-[var(--aura-charcoal)] text-[var(--aura-ivory)] hover:bg-[var(--aura-teal)] transition-colors"
                  >
                    {ev.ctaLabel}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
